import Table from "../../common/components/Table";
import Checkbox from "../../common/components/Checkbox";
import { formatDate } from "../../../utilities/format";
import { ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { Quiz } from "../container/QuizManagementContainer";

interface QuizTableProps {
  quizzes: Quiz[];
  selectedIds: string[];
  onSelectAll: (selected: boolean) => void;
  onSelectQuiz: (quizId: string, selected: boolean) => void;
  onQuizClick?: (quiz: Quiz) => void;
  onViewDocument?: (quiz: Quiz) => void;
  isLoading?: boolean;
  sortField?: string;
  sortOrder?: "ASC" | "DESC";
  onSort?: (key: string) => void;
}

export default function QuizTable({
  quizzes,
  selectedIds,
  onSelectAll,
  onSelectQuiz,
  onQuizClick,
  onViewDocument,
  isLoading,
  sortField,
  sortOrder,
  onSort,
}: QuizTableProps) {
  const isAllSelected = quizzes.length > 0 && selectedIds.length === quizzes.length;

  // Map API field names back to UI keys for comparison
  const fieldToKeyMap: { [key: string]: string } = {
    "AWARDED_POINTS": "points",
    "CREATED_AT": "createdAt",
    "UPDATED_AT": "updatedAt",
  };

  const currentSortKey = sortField ? fieldToKeyMap[sortField] : "";

  const SortableHeader = ({
    sortKey: key,
    children,
    className,
    style
  }: {
    sortKey?: string;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
  }) => {
    // Only allow sorting for points, createdAt, updatedAt
    const sortableKeys = ["points", "createdAt", "updatedAt"];
    const isSortable = key && sortableKeys.includes(key) && onSort;
    const isActive = currentSortKey === key;

    if (!isSortable) {
      return <div className={className} style={style}>{children}</div>;
    }

    const getSortIcon = () => {
      if (isActive) {
        return sortOrder === "ASC" ? (
          <ChevronUpIcon className="h-4 w-4 ml-1" />
        ) : (
          <ChevronDownIcon className="h-4 w-4 ml-1" />
        );
      }
      return <ChevronUpDownIcon className="h-4 w-4 ml-1" />;
    };

    return (
      <div
        className={`${className} cursor-pointer transition-colors hover:bg-gray-800 ${
          isActive ? 'bg-gray-800' : ''
        }`}
        style={style}
        onClick={() => onSort(key)}
      >
        {children}
        {getSortIcon()}
      </div>
    );
  };

  const getStatusDisplay = (status: Quiz["status"]) => {
    switch (status) {
      case "활성":
        return <span className="px-3 py-1 rounded-md border text-xs font-bold bg-green-100 border-green-500 text-green-600">활성</span>;
      case "비활성":
        return <span className="px-3 py-1 rounded-md border text-xs font-bold bg-red-100 border-red-300 text-red-500">비활성</span>;
      case "신규":
        return <span className="px-3 py-1 rounded-md border text-xs font-bold bg-yellow-100 border-yellow-500 text-yellow-600">신규</span>;
      case "반려":
        return <span className="px-3 py-1 rounded-md border text-xs font-bold bg-gray-100 border-gray-400 text-gray-700">반려</span>;
      default:
        return <span>{status}</span>;
    }
  };

  return (
    <div className="relative h-full flex flex-col">
      <Table.Wrapper className="flex-1">
        <div className="flex w-full items-center rounded-t-lg border border-black bg-black py-2">
          <div className="flex items-center justify-center px-2 text-sm font-bold text-white" style={{ width: '63px', minWidth: '63px' }}>
            <Checkbox
              isChecked={isAllSelected}
              onCheck={onSelectAll}
            />
          </div>
          <SortableHeader
            sortKey="status"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '100px', minWidth: '100px' }}
          >
            상태
          </SortableHeader>
          <SortableHeader
            sortKey="title"
            className="flex items-center justify-center px-2 text-sm font-bold text-white flex-1"
            style={{ minWidth: '200px' }}
          >
            제목
          </SortableHeader>
          <SortableHeader
            sortKey="type"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '100px', minWidth: '100px' }}
          >
            유형
          </SortableHeader>
          <SortableHeader
            sortKey="difficulty"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '100px', minWidth: '100px' }}
          >
            난이도
          </SortableHeader>
          <SortableHeader
            sortKey="points"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '100px', minWidth: '100px' }}
          >
            포인트
          </SortableHeader>
          <SortableHeader
            sortKey="createdAt"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '105px', minWidth: '105px' }}
          >
            등록일자
          </SortableHeader>
          <SortableHeader
            sortKey="updatedAt"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '105px', minWidth: '105px' }}
          >
            수정일자
          </SortableHeader>
          <SortableHeader
            sortKey="lastModifier"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '120px', minWidth: '120px' }}
          >
            최종수정자
          </SortableHeader>
          <div
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '80px', minWidth: '80px' }}
          >
            기반자료
          </div>
          <SortableHeader
            sortKey="quizNumber"
            className="flex items-center justify-center px-2 text-sm font-bold text-white"
            style={{ width: '100px', minWidth: '100px' }}
          >
            생성번호
          </SortableHeader>
        </div>

        <Table.Body isLoading={isLoading} isEmpty={!quizzes?.length}>
          {quizzes?.map((quiz) => {
            const isSelected = selectedIds.includes(quiz.quizId);

            return (
              <div
                key={quiz.quizId}
                className="relative cursor-pointer hover:bg-gray-50"
                data-quiz-id={quiz.quizId}
                onClick={() => onQuizClick?.(quiz)}
              >
                <Table.Row>
                  <div
                    className="flex items-center justify-center border-e px-2"
                    style={{ width: '63px', minWidth: '63px' }}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={isSelected}
                        onCheck={(checked: boolean) => onSelectQuiz(quiz.quizId, checked)}
                      />
                    </div>
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '100px', minWidth: '100px' }}
                  >
                    {getStatusDisplay(quiz.status)}
                  </div>

                  <div
                    className="flex items-center justify-start border-e px-2 flex-1"
                    style={{ minWidth: '200px' }}
                  >
                    <span className="truncate" title={quiz.title}>
                      {quiz.title}
                    </span>
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '100px', minWidth: '100px' }}
                  >
                    {quiz.type}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '100px', minWidth: '100px' }}
                  >
                    {quiz.difficulty}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '100px', minWidth: '100px' }}
                  >
                    {quiz.points}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '105px', minWidth: '105px' }}
                  >
                    {formatDate(quiz.createdAt, "YY.MM.DD")}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '105px', minWidth: '105px' }}
                  >
                    {formatDate(quiz.updatedAt, "YY.MM.DD")}
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '120px', minWidth: '120px' }}
                  >
                    <span className="truncate" title={quiz.lastModifier}>
                      {quiz.lastModifier}
                    </span>
                  </div>

                  <div
                    className="flex items-center justify-center border-e px-2 text-sm"
                    style={{ width: '80px', minWidth: '80px' }}
                  >
                    {quiz.sourceMaterialId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDocument?.(quiz);
                        }}
                        className="p-2 hover:bg-gray-200 rounded transition-colors"
                        title="기반자료 보기"
                      >
                        <PaperAirplaneIcon className="h-5 w-5 text-gray-600" />
                      </button>
                    )}
                  </div>

                  <div
                    className="flex items-center justify-center px-2 text-sm"
                    style={{ width: '100px', minWidth: '100px' }}
                  >
                    {quiz.generationJobId || '-'}
                  </div>
                </Table.Row>
              </div>
            );
          })}
        </Table.Body>
      </Table.Wrapper>
    </div>
  );
}